import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BrandSymbol from '../ui/BrandSymbol';
import { API_BASE_URL } from '../../config/api';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { resolvePostLoginRoute } from '../../utils/postLoginRouting';

const MFA_CODE_LENGTH = 8;
const RESEND_COOLDOWN = 60; // seconds

const MFAVerification: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useSessionPersistence();
  
  // Get pending MFA data from sessionStorage (set by login page)
  const [userId] = useState(() => sessionStorage.getItem('mfa_userId') || '');
  const [userEmail] = useState(() => sessionStorage.getItem('mfa_email') || '');
  
  // Code input state - array of 8 characters
  const [codeDigits, setCodeDigits] = useState<string[]>(Array(MFA_CODE_LENGTH).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  // UI state
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [magicLinkVerifying, setMagicLinkVerifying] = useState(false);

  // Check for magic link token in URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyMagicLink(token);
    }
  }, [searchParams]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // Redirect if no pending MFA session
  useEffect(() => {
    const token = searchParams.get('token');
    if (!userId && !token) {
      console.log('[MFA] No pending MFA session, redirecting to login');
      navigate('/login');
    }
  }, [userId, searchParams, navigate]);

  // (FIX)
  const acceptPendingExecutorInvite = async (accountToken: string) => {
    const pendingInviteToken = sessionStorage.getItem('pending_invite_token');
    if (!pendingInviteToken) return;

    const pendingInviteRole = sessionStorage.getItem('pending_invite_role') ?? 'executor';
    const endpoint = pendingInviteRole === 'beneficiary'
      ? '/api/beneficiaries/accept-invite'
      : '/api/executors/accept-invite';

    try {
      const resp = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accountToken}`
        },
        body: JSON.stringify({ inviteToken: pendingInviteToken })
      });

      if (resp.ok) {
        console.log(`[MFA] Successfully accepted pending ${pendingInviteRole} invite`);
        sessionStorage.removeItem('pending_invite_token');
        sessionStorage.removeItem('pending_invite_role');
      } else {
        const errJson = await resp.json().catch(() => ({}));
        console.error('[MFA] Failed to accept pending invite:', errJson);
        // Keep token so user can retry by re-opening invite link if needed
      }
    } catch (err) {
      console.error('[MFA] Error accepting pending invite:', err);
      // Keep token so user can retry
    }
  };

  const navigatePostMFA = async (accountToken: string) => {
    const route = await resolvePostLoginRoute(accountToken);

    if (route.state) {
      navigate(route.path, { state: route.state });
    } else {
      navigate(route.path);
    }
  };


  const verifyMagicLink = async (token: string) => {
    console.log('[MFA] verifyMagicLink called with token:', token.substring(0, 20) + '...');
    setMagicLinkVerifying(true);
    setError('');

    try {
      console.log('[MFA] Sending magic link verification to:', `${API_BASE_URL}/api/auth/mfa/magic/${token.substring(0, 20)}...`);
      const response = await fetch(`${API_BASE_URL}/api/auth/mfa/magic/${token}`);
      console.log('[MFA] Magic link response status:', response.status);
      const data = await response.json();
      console.log('[MFA] Magic link response data:', JSON.stringify(data));

      if (data.success) {
        console.log('[MFA] Magic link verification successful');
        console.log('[MFA] Token received:', data.token ? 'YES' : 'NO');
        console.log('[MFA] User received:', data.user ? JSON.stringify(data.user) : 'NO');
        
        // Clear MFA session data
        sessionStorage.removeItem('mfa_userId');
        sessionStorage.removeItem('mfa_email');
        sessionStorage.removeItem('mfa_name');

        // Login with token
        console.log('[MFA] Calling login function from magic link...');
        try {
          await login(data.token, data.user);
          await acceptPendingExecutorInvite(data.token); // fix
          console.log('[MFA] Login function completed successfully (magic link)');
        } catch (loginErr) {
          console.error('[MFA] Login function threw error (magic link):', loginErr);
          throw loginErr;
        }
        
        await navigatePostMFA(data.token);
      } else {
        // Magic link failed - redirect to login with error
        console.log('[MFA] Magic link failed:', data.message);
        // Store error in sessionStorage to show on login page
        sessionStorage.setItem('login_error', data.message || 'Your verification link has expired. Please log in again to receive a new code.');
        navigate('/login');
      }
    } catch (err) {
      console.error('[MFA] Magic link error:', err);
      // Redirect to login with generic error
      sessionStorage.setItem('login_error', 'Verification link invalid. Please log in again to receive a new code.');
      navigate('/login');
    }
  };

  const handleInputChange = (index: number, value: string) => {
    // Only allow alphanumeric characters
    const char = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    
    if (char.length <= 1) {
      const newDigits = [...codeDigits];
      newDigits[index] = char;
      setCodeDigits(newDigits);

      // Auto-advance to next input
      if (char && index < MFA_CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }

      // Auto-submit when all digits are filled
      if (char && index === MFA_CODE_LENGTH - 1) {
        const fullCode = newDigits.join('');
        if (fullCode.length === MFA_CODE_LENGTH) {
          handleVerify(fullCode);
        }
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < MFA_CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').toUpperCase().replace(/[^A-Z0-9]/g, '');
    const chars = pastedText.slice(0, MFA_CODE_LENGTH).split('');
    
    const newDigits = [...codeDigits];
    chars.forEach((char, i) => {
      if (i < MFA_CODE_LENGTH) {
        newDigits[i] = char;
      }
    });
    setCodeDigits(newDigits);

    // Focus on the next empty input or last input
    const nextEmptyIndex = newDigits.findIndex(d => !d);
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[MFA_CODE_LENGTH - 1]?.focus();
      // Auto-submit if all digits filled
      if (newDigits.join('').length === MFA_CODE_LENGTH) {
        handleVerify(newDigits.join(''));
      }
    }
  };

  const handleVerify = async (code?: string) => {
    const verificationCode = code || codeDigits.join('');
    console.log('[MFA] handleVerify called with code length:', verificationCode.length);
    console.log('[MFA] userId:', userId);
    
    if (verificationCode.length !== MFA_CODE_LENGTH) {
      setError('Please enter the complete 8-character code');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      console.log('[MFA] Sending verification request to:', `${API_BASE_URL}/api/auth/mfa/verify`);
      const response = await fetch(`${API_BASE_URL}/api/auth/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          code: verificationCode
        })
      });

      console.log('[MFA] Response status:', response.status);
      const data = await response.json();
      console.log('[MFA] Response data:', JSON.stringify(data));

      if (data.success) {
        console.log('[MFA] Verification successful, calling login...');
        console.log('[MFA] Token received:', data.token ? 'YES' : 'NO');
        console.log('[MFA] User received:', data.user ? JSON.stringify(data.user) : 'NO');
        
        // Clear MFA session data
        sessionStorage.removeItem('mfa_userId');
        sessionStorage.removeItem('mfa_email');
        sessionStorage.removeItem('mfa_name');

        // Login with token
        console.log('[MFA] Calling login function...');
        try {
          await login(data.token, data.user);
          await acceptPendingExecutorInvite(data.token); // fix
          console.log('[MFA] Login function completed successfully');
        } catch (loginErr) {
          console.error('[MFA] Login function threw error:', loginErr);
          throw loginErr;
        }
        
        await navigatePostMFA(data.token);
      } else {
        console.log('[MFA] Verification failed:', data.message);
        setError(data.message || 'Invalid verification code');
        setCodeDigits(Array(MFA_CODE_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      console.error('[MFA] Verify error:', err);
      setError('Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || isResending) return;

    setIsResending(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/mfa/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });

      const data = await response.json();

      if (data.success) {
        setResendCooldown(RESEND_COOLDOWN);
        setCodeDigits(Array(MFA_CODE_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
      } else {
        if (data.waitSeconds) {
          setResendCooldown(data.waitSeconds);
        }
        setError(data.message || 'Failed to resend code');
      }
    } catch (err) {
      console.error('[MFA] Resend error:', err);
      setError('Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  // Show loading if verifying magic link
  if (magicLinkVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-white text-lg">Verifying your login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-900 to-slate-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center justify-center w-full mb-8">
          <BrandSymbol size="4xl" showText={true} showTagline={true} variant="light" className="mx-auto" />
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-cyan-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-stone-800 mb-2">Verify Your Login</h1>
            <p className="text-stone-600">
              We sent a verification code to<br />
              <span className="font-medium text-cyan-700">{userEmail}</span>
            </p>
          </div>

          {/* Code input boxes */}
          <div className="flex justify-center gap-2 mb-6">
            {codeDigits.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="w-10 h-12 text-center text-xl font-mono font-bold border-2 border-stone-300 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all uppercase"
                disabled={isVerifying}
              />
            ))}
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-center text-sm">
              {error}
            </div>
          )}

          {/* Verify button */}
          <button
            onClick={() => handleVerify()}
            disabled={isVerifying || codeDigits.join('').length !== MFA_CODE_LENGTH}
            className={`w-full py-3 rounded-lg font-semibold text-white transition-all ${
              isVerifying || codeDigits.join('').length !== MFA_CODE_LENGTH
                ? 'bg-stone-300 cursor-not-allowed'
                : 'bg-cyan-700 hover:bg-cyan-800'
            }`}
          >
            {isVerifying ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Verifying...
              </span>
            ) : (
              'Verify Code'
            )}
          </button>

          {/* Resend link */}
          <div className="text-center mt-4">
            <p className="text-stone-500 text-sm mb-2">Didn't receive the code?</p>
            <button
              onClick={handleResend}
              disabled={resendCooldown > 0 || isResending}
              className={`text-sm font-medium ${
                resendCooldown > 0 || isResending
                  ? 'text-stone-400 cursor-not-allowed'
                  : 'text-cyan-700 hover:text-cyan-800 hover:underline'
              }`}
            >
              {isResending ? (
                'Sending...'
              ) : resendCooldown > 0 ? (
                `Resend code in ${resendCooldown}s`
              ) : (
                'Resend verification code'
              )}
            </button>
          </div>

          {/* Back to login */}
          <div className="text-center mt-6 pt-4 border-t border-stone-200">
            <button
              onClick={() => {
                sessionStorage.removeItem('mfa_userId');
                sessionStorage.removeItem('mfa_email');
                sessionStorage.removeItem('mfa_name');
                sessionStorage.removeItem('pending_invite_token'); //small ui fix
                sessionStorage.removeItem('pending_invite_role');
                navigate('/login');
              }}
              className="text-stone-500 hover:text-stone-700 text-sm"
            >
              ← Back to login
            </button>
          </div>
        </div>

        {/* Help text */}
        <p className="text-center text-stone-400 text-sm mt-6">
          Check your spam folder if you don't see the email.<br />
          The code expires in 10 minutes.
        </p>
      </div>
    </div>
  );
};

export default MFAVerification;

